import * as v from '@badrap/valita'
import { api } from 'src/api'
import { Region } from 'src/regions'
import { encrypt } from 'src/utils/crypto'

export const registerNewSubscriber = (params: {
  organization: string
  username: string
  email: string
  phone: string
  regToken: string
  userAmount: string
  industry: string
  allowFeedback: boolean
  backendRegion: Region
}) => {
  return api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/registerNewSubscriber',
    body: {
      ...params,
      check: encrypt(params.organization),
    },
    region: params.backendRegion,
    withAuth: false,
  })
}

export const createSetupCognitoUser = (params: {
  organization: string
  username: string
  email: string
  phone: string
  firstname: string
  lastname: string
  region: Region
}) => {
  return api({
    schema: v
      .object({
        result: v.literal('OK'),
        user: v.object({
          User: v.object({
            Username: v.string(),
            Attributes: v.array(
              v.object({
                Name: v.string(),
                Value: v.string(),
              }),
            ),
            UserCreateDate: v.string(),
            UserLastModifiedDate: v.string(),
            Enabled: v.boolean(),
            UserStatus: v.union(
              v.literal('CONFIRMED'),
              v.literal('FORCE_CHANGE_PASSWORD'),
            ),
          }),
        }),
      })
      .chain((data) => v.ok(data.user)),
    method: 'POST',
    url: '/createsetupcognitouser',
    body: {
      ...params,
      check: encrypt(params.organization),
    },
    region: params.region,
    withAuth: false,
  })
}

export const addOrganization = (params: {
  organization: string
  setupuserPoolRegion: string
  setupuserPoolId: string
  setupuserPoolAppClientId: string
  setupStep: 'setup' | 'salesforce-configuration' | 'finished'
  application?: Record<string, string | Record<string, string>>
  backendRegion: Region
}) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/addorganization',
    body: {
      ...params,
      code: params.organization,
      check: encrypt(params.organization),
      backendRegion: undefined, // shouldn't be stored in this DB-table
    },
    withAuth: false,
    region: params.backendRegion,
  })

export type EditOrganization = {
  billing?:
    | 'free'
    | 'billable'
    | 'trial'
    | 'custom'
    | 'contract'
    | 'contract_trial'
  organization: string
  username: string
  application?: Record<
    string,
    string | number | boolean | Record<string, string>
  >
  aws_connect_saml_url?: string
  aws_connect_type?: 'CCP' | 'SAML' | 'SSO' | 'SALESFORCE'
  code?: string
  cognito_setup_url?: string
  cognito_url?: string
  failedAgentConnectTimer?: string
  iam_app_logging_accessKey?: string
  iam_app_logging_accessSecret?: string
  iam_app_logging_region?: string
  iam_organization_admin_accessKey?: string
  iam_organization_admin_accessSecret?: string
  iam_organization_admin_region?: string
  iam_organization_policy_arn?: string
  iam_organization_usergroup?: string
  iam_organizaton_api_version?: string
  organization_APNS_topic?: string
  region?: string
  organization_connect_arn?: string
  organization_connect_account_id?: string
  organization_connect_instance_id?: string
  redirect_uri?: string
  setupStep?: 'setup' | 'salesforce-configuration' | 'finished'
  setupuserPoolAppClientId?: string
  setupuserPoolClientSecret?: string
  setupuserPoolId?: string
  setupuserPoolRegion?: string
  userPoolAppClientId?: string
  userPoolClientSecret?: string
  userPoolId?: string
  userPoolRegion?: string
  userPoolTokenType?: string
  voippushserver_apnsKeyId?: string
  voippushserver_apnTeamId?: string
  voippushserver_production?: string
  voipserver_apnsp8_binary?: string
  sso?: {
    loginUrl: string
    principalArn: string
    region: string
    roleArn: string
    type: 'salesforce' | 'azure' | 'awsiam' | 'googleworkspace'
  }
}

export const editOrganization = (params: EditOrganization) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/editorganization',
    body: params,
    withAuth: true,
  })

export const reloadOneOrganization = (params: { organization: string }) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/broadcast/reloadoneorganization',
    body: {
      ...params,
      check: encrypt(params.organization),
    },
    withAuth: false,
  })

export const reloadOrganizations = () =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/broadcast/reloadorganizations',
    body: {},
    withAuth: true,
  })

export const salesforceConnectionTest = (params: {
  username: string
  organization: string
  sf_clientId: string
  sf_clientSecret: string
  sf_username: string
  sf_password: string
}) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/salesforce/connectiontest',
    body: params,
    withAuth: true,
  })
